import React, { useRef, useState } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import Text from '../tenant_portal/fields/Text';
import type { FieldsDeepUpdate } from '../typescript/types/FieldComponentProps';
import { FIELD_MODES } from '../components/fields/FieldTools';
import PortalButton from '../components/PortalButton';
import PortalAuxButton from '../components/PortalAuxButton';
import type { ErrorType } from '../components/Types';
import { filterErrorsByPath } from '../components/Tools';
import PageHeader from '../tenant_portal/shared/PageHeader';
import loginStyles from '../../../stylesheets/components/login.module.scss';
import Alert from '../components/Alert';
import SomethingWentWrongAlert from './SomethingWentWrongAlert';
import { getPreloadedErrorMessages } from './ErrorTools';

interface LoginEmailTwoFactorPortalData {
  expiresInSeconds: number;
  loginEmail: string;
  sandboxCode?: string;
  signOutPath: string;
  smsPath: string;
  smsSuccessMessage: string | null;
  verifyPath: string;
}

const EmailTwoFactor = () => {
  const emailTwoFactorData = (
    LOGIN_PORTAL_DATA as LoginPortalData & {
      emailTwoFactor?: LoginEmailTwoFactorPortalData;
    }
  ).emailTwoFactor;

  if (!emailTwoFactorData) {
    throw new Error(
      'Expected LOGIN_PORTAL_DATA.emailTwoFactor for /login/email-two-factor.'
    );
  }

  const formRef = useRef<HTMLFormElement>(null);
  const smsFormRef = useRef<HTMLFormElement>(null);
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<ErrorType[]>([]);
  const [serverErrorMessage, setServerErrorMessage] = useState(
    getPreloadedErrorMessages(preloadedErrors).join(' ')
  );
  const {
    expiresInSeconds,
    loginEmail,
    sandboxCode,
    signOutPath,
    smsPath,
    smsSuccessMessage: initialSmsSuccessMessage,
    verifyPath,
  } = emailTwoFactorData;
  const [smsSuccessMessage, setSmsSuccessMessage] = useState(
    initialSmsSuccessMessage
  );
  const expiresInMinutes = Math.max(1, Math.ceil(expiresInSeconds / 60));

  const handleCodeChange: FieldsDeepUpdate = (_fieldName, value) => {
    setCode(value?.toString() || '');
    if (errors.length > 0) setErrors([]);
    if (serverErrorMessage !== '') setServerErrorMessage('');
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (code.trim() !== '') return;

    event.preventDefault();
    setErrors([
      {
        path: 'code',
        messages: ["can't be blank"],
      },
    ]);
  };

  return (
    <div className={loginStyles.loginRouteContent}>
      <PageHeader title="Verify your login" includeBottomMargin={false} />
      <p className={loginStyles.loginSubtitle}>
        Enter the 6-digit verification code we emailed to {loginEmail}. It
        expires in about {expiresInMinutes} minute
        {expiresInMinutes === 1 ? '' : 's'}.
      </p>
      {sandboxCode && (
        <p className={loginStyles.loginSubtitle}>
          Sandbox verification code: {sandboxCode}
        </p>
      )}
      {smsSuccessMessage && (
        <Alert
          dataTestClass="login-sms-success-banner"
          onClose={() => setSmsSuccessMessage(null)}
          severity="success"
          title="Verification code sent"
        >
          {smsSuccessMessage}
        </Alert>
      )}
      <SomethingWentWrongAlert
        body={serverErrorMessage}
        onDismiss={() => setServerErrorMessage('')}
        resetKey={serverErrorMessage}
      />
      <form
        ref={formRef}
        action={verifyPath}
        className={loginStyles.loginForm}
        method="post"
        onSubmit={handleFormSubmit}
      >
        <input name="authenticity_token" type="hidden" value={AUTH_TOKEN} />
        <Text
          field={{
            label: 'Verification code',
            name: 'code',
            value: code,
            autocomplete: 'one-time-code',
            autoFocus: true,
            inputMode: 'numeric',
            maxLength: 6,
            pattern: '\\d*',
          }}
          mode={FIELD_MODES.EDIT}
          onChange={handleCodeChange}
          errors={filterErrorsByPath(errors, ['code'])}
        />
        <FormControlLabel
          className={loginStyles.loginTrustDeviceLabel}
          control={
            <Checkbox
              className={loginStyles.loginTrustDeviceCheckbox}
              name="trust_device"
              size="small"
              value="1"
            />
          }
          label="Don’t ask again on this computer"
        />
        <button hidden type="submit" /> {/* Allows submit on enter */}
      </form>
      <PortalButton
        extraClasses={[loginStyles.loginPrimaryButton]}
        onClick={() => formRef.current?.requestSubmit()}
      >
        Verify code
      </PortalButton>
      <form ref={smsFormRef} action={smsPath} hidden method="post">
        <input name="authenticity_token" type="hidden" value={AUTH_TOKEN} />
      </form>
      <PortalAuxButton
        extraClasses={[loginStyles.loginAuxButton]}
        onClick={() => smsFormRef.current?.requestSubmit()}
      >
        Didn&apos;t receive code? Send it by text
      </PortalAuxButton>
      <PortalAuxButton
        extraClasses={[loginStyles.loginAuxButton]}
        onClick={() => {
          window.location.href = signOutPath;
        }}
      >
        Log out
      </PortalAuxButton>
    </div>
  );
};

export default EmailTwoFactor;
